import {Field, Form, Formik} from 'formik'
import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {useThemeMode} from '../../../../../_metronic/partials'
import {createSettings} from '../../core/_requests'
import {useAuth} from '../../../auth'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {SettingsInputPeriod} from '../../../widgets/components/Input/SettingsInputPeriod'
import {archivalSettingsOptions} from '../../core/_constants'
import Skeleton from 'react-loading-skeleton'

const LegalHeld = ({aiEmailData, fetchAiEmailDataWithoutLoader, aiEmailLoading}: any) => {
  const {formatMessage} = useIntl()
  const {companyId} = useAuth()
  const {mode} = useThemeMode()
  const [loading, setLoading] = useState<boolean>(false)

  const initialValues = {
    islegalHoldExtension: aiEmailData?.legalHold || false,
    // roleTags: aiEmailData?.legalRole || '',
    legalHoldExtensionValue: aiEmailData?.legalHoldValue || '',
    legalHoldExtensionPeriod: aiEmailData?.legalHoldPeriod || '',
  }

  const validationSchema = Yup.object().shape({
    islegalHoldExtension: Yup.boolean(),

    legalHoldExtensionPeriod: Yup.string()
      .nullable()
      .when('islegalHoldExtension', {
        is: true,
        then: (schema) => schema.required('LegalHoldExpiryPeriod is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    legalHoldExtensionValue: Yup.number()
      .nullable()
      .test(
        'is-integer',
        'Legal value must be an integer number',
        (value) => value === undefined || value === null || Number.isInteger(value)
      )
      .when(['islegalHoldExtension', 'legalHoldExtensionPeriod'], {
        is: (islegalHoldExtension: any, legalHoldExtensionPeriod: any) =>
          islegalHoldExtension && ['Days', 'Years', 'Months'].includes(legalHoldExtensionPeriod),
        then: (schema) =>
          schema
            .min(1, 'LegalHoldValue cannot be negative or 0')
            .typeError('LegalHoldExpiryValue must be a number')
            .required('LegalHoldExpiryValue is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
  })

  const handleSubmit = async (values: any, {resetForm}: any) => {
    if (!companyId) return
    setLoading(true)
    const payload = {
      legalHold: values?.islegalHoldExtension,
      // legalRole: values?.roleTags,
      legalHoldValue: parseInt(values?.legalHoldExtensionValue),
      legalHoldPeriod: values?.legalHoldExtensionPeriod,
    }
    try {
      const {
        data: {success, errors},
      } = await createSettings(companyId, payload)
      if (success) {
        toast.success('Setting saved successfully')
        fetchAiEmailDataWithoutLoader()
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          enableReinitialize={true}
          validateOnChange
        >
          {(formik) => (
            <Form>
              <div className='row'>
                <p className='fw-semibold m-0'>{formatMessage({id: 'Documents legal holds'})}</p>
                <div className='col-md-12 col-12 py-2 my-4'>
                  <div className='form-check form-check-custom form-check-solid form-check-primary form-switch'>
                    <label className='togglebtn mx-0'>
                      <Field
                        className='togglebtn__input'
                        name='islegalHoldExtension'
                        type='checkbox'
                        disabled={aiEmailLoading}
                        formik={formik}
                        onChange={(e: any) => {
                          formik.handleChange(e)
                          const isChecked = e.target.checked

                          if (!isChecked) {
                            formik.setFieldValue('legalHoldExpiryValue', '')
                            formik.setFieldValue('legalHoldExpiryPeriod', '')
                          }
                        }}
                      />
                      <div className='togglebtn__fill'></div>
                    </label>
                    <div className='d-flex flex-column justify-content-end mt-2'>
                      <div className='d-flex'>
                        <div>
                          <label
                            className='form-check-label fw-semibold font-size-13 title-notification'
                            data-bs-toggle='tooltip'
                          >
                            {formatMessage({id: 'Legal Hold'})}
                          </label>
                        </div>
                      </div>
                      <label
                        className='form-check-label text-clr58 fw-normal fs-7'
                        data-bs-toggle='tooltip'
                      >
                        {formatMessage({
                          id: 'Turn this “On” to allow users to mark certain documents as "On Legal Hold" to prevent them from being modified or deleted.',
                        })}
                      </label>
                    </div>
                  </div>
                </div>
                <div className='col-12 col-md-12'>
                  <div
                    className='p-2 rounded mb-8'
                    style={
                      mode === 'dark'
                        ? {background: '#6C4F2C', border: '2px dashed #FFC700'}
                        : {background: '#FFF8DD', border: '2px dashed #FFC700'}
                    }
                  >
                    <p className='m-0 '>
                      <span className='fw-bold'>{formatMessage({id: 'Note: '})}</span>
                      {formatMessage({
                        id: 'In case of a retention period conflict, the higher limit will apply. For example, if the department-based retention period is set to 10 Years but the retention period for PDF files is 7 Years, the final retention period for for PDFs in the Finance department will be 10 Years.',
                      })}
                    </p>
                  </div>
                </div>
                <div className='col-md-6 col-12'>
                  {aiEmailLoading ? (
                    <div className='d-flex flex-row my-3'>
                      <div className='w-25 me-5'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                      <div className='w-75'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                    </div>
                  ) : (
                    <SettingsInputPeriod
                      label={formatMessage({id: 'Max Legal Hold Expiry Period Allowed'})}
                      toolTipText={formatMessage({
                        id: `Choose the number of years for which the authorized roles can apply a legal hold on records and files.`,
                      })}
                      fieldName='legalHoldExtensionValue'
                      placeholder={''}
                      placeholderSelect={''}
                      disabled={!formik.values.islegalHoldExtension}
                      formik={formik}
                      isTooltipForModal={true}
                      options={archivalSettingsOptions}
                      selectOptionFieldName='legalHoldExtensionPeriod'
                      inputDisable={true}
                    />
                  )}
                </div>
                <div className='d-flex justify-content-end'>
                  <button
                    type='submit'
                    className='btn bg-primary text-white custom-button-property border border-1 mb-md-4 mb-0 px-6 py-4 text-center w-100 w-md-auto order-1 order-md-2'
                    disabled={(!formik.isValid && formik.values.islegalHoldExtension) || loading}
                  >
                    {!loading && (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({id: 'Save Changes'})}
                      </span>
                    )}
                    {loading && (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({id: 'Please wait...'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </>
    </div>
  )
}

export default LegalHeld
